import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Spin from 'components/Spin';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateMomentFormat } from 'v2/formatters/date';
import { ETicketStatus } from 'v2/interfaces/help';
import helpService from 'v2/services/help';

import { useStyles } from './styles';
import { historyModalSchema } from './validation';

import type { FormikProps } from 'formik';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface IFormikValues {
  comment: string;
}

const HistoryModal = ({ isOpen, handleClose }: IProps) => {
  const classes = useStyles();

  const historyChatRef = useRef<HTMLDivElement>();

  const [loadingTicket] = useObservable(() => helpService.getLoadingTicket(), []);
  const [selectedTicket] = useObservable(() => helpService.getTicket(), []);

  const onSubmit = ({ comment }: IFormikValues, { setSubmitting }: FormikProps<IFormikValues>) => {
    helpService.sendComment(selectedTicket.id, comment).subscribe(
      () => {
        toast.success('Comentário enviado com sucesso!');
        setSubmitting(false);
      },
      () => {
        toast.error('Erro ao enviar comentário!');
        setSubmitting(false);
      }
    );
  };

  useEffect(() => {
    const historyChatNode = historyChatRef.current;
    if (historyChatNode) {
      historyChatNode.scrollTop = historyChatNode.scrollHeight - historyChatNode.clientHeight;
    }
  }, [loadingTicket]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle onClose={handleClose}>
        <Typography variant='h6'>Histórico do ticket</Typography>
      </DialogTitle>
      {loadingTicket && <Spin />}
      {!loadingTicket && (
        <DialogContent>
          <Typography variant='body1'>
            <Truncate count={50} final='...'>
              {selectedTicket?.title}
            </Truncate>
          </Typography>
          <div className='m8' />
          <div ref={historyChatRef} className={classes.historicChat}>
            {selectedTicket?.comments?.map((comment) => (
              <div key={'message-' + comment.id} className={`${classes.historicMessage} ${comment.self && '--producer'}`}>
                {!comment.self && <Typography className={classes.historicName}>Suporte Eduzz</Typography>}
                <NoDangerouslyHTML className={`${classes.historicContent} ${comment.self && '--producer'}`} html={comment.content} />
                <div className={`${classes.historicDate} ${comment.self && '--producer'}`}>
                  {dateMomentFormat(comment.created_at, 'DD/MM/YYYY HH:mm')}
                </div>
              </div>
            ))}
          </div>
          <div className='m16' />
          {selectedTicket?.status !== ETicketStatus.Fechado && (
            <div className={classes.historicAnswer}>
              <Formik initialValues={{ comment: '' }} onSubmit={onSubmit} validationSchema={historyModalSchema}>
                {({ dirty, isValid, isSubmitting }) => (
                  <Form>
                    <Field
                      name='comment'
                      placeholder='Digite um comentário...'
                      fullWidth
                      multiline
                      rows={1}
                      rowsMax={1}
                      inputProps={{
                        maxLength: 500,
                      }}
                      component={TextFormField}
                    />
                    <div className='m16' />
                    <Button variant='contained' color='secondary' disabled={isSubmitting || !dirty || !isValid} type='submit'>
                      Comentar
                      {isSubmitting && <CircularProgress size={16} style={{ marginLeft: 10 }} />}
                    </Button>
                    <div className='m8' />
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default HistoryModal;
